import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ChatBot from "./ChatBot"; // Import the ChatBot component
import colors from "../../../Constants/colors";
import {
  DeployedBotsContainerStyles,
  DeployedBotsPaperStyles,
  DeployedBotsTableContainerStyles,
} from "./DeployedBotsStyles";
import { useDispatch, useSelector } from "react-redux";
import {
  getBotsbyUser,
  getCombinationDetails,
  saveDefaultCombinations,
} from "../../../features/chatbots/chatbotSlice";
import { useNavigate } from "react-router-dom";
import { Chat, Delete, ViewTimeline } from "@mui/icons-material";
import { dialogStyles } from "../../../Constants/dialogStyles";
import {
  primaryButtonStyles,
  secondaryButtonStyles,
} from "../../../Constants/buttonStyles";
import theme from "../../../Constants/theme";
import { tableStyles } from "../../../Constants/tableStyles";

const DeployedBots = () => {
  const [showChat, setShowChat] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [deployedBotsList, setdeployedBotsList] = useState([]);
  const [botLoading, setBotLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [botToDelete, setBotToDelete] = useState(null);
  const [showSelectedCombinations, setShowSelectedCombinations] =
    useState(false);
  const [showBotCombination, setShowBotCombination] = useState(null);
  const [botLoadingState, setBotLoadingState] = useState({});
  const isAnyBotLoading = Object.values(botLoadingState).some(
    (loading) => loading
  );
  const { organization } = useSelector((state) => state.organization);
  const { user } = useSelector((state) => state.auth);
  const chatbot = useSelector((state) => state.chatbot);

  const isOrgLoading = !organization || !organization._id;
  const isBotsLoading = botLoading || isOrgLoading;

  const [versionPickerOpen, setVersionPickerOpen] = useState(false);
  const [pickerAction, setPickerAction] = useState(""); // "view" or "delete"
  const [actionBot, setActionBot] = useState(null); // which bot we’re acting on
  const [deployedVersions, setDeployedVersions] = useState([]); // array of {id, name, updated_at, retrieval, generation}
  const [selectedVersionId, setSelectedVersionId] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const check = organization?.bot.length > 0;
    const user_id = user?.user?._id?.["$oid"];
    const templates_bots_check = organization?.templates.length;
    const chatbots_check = chatbot?.chatbots?.combinations?.length;

    const get_bots = async () => {
      if (!user_id) return;

      setBotLoading(true);
      try {
        await dispatch(getBotsbyUser(user_id));
      } catch (error) {
        console.error("Error fetching bots:", error);
      } finally {
        setBotLoading(false);
      }
    };
    if (
      check &&
      (templates_bots_check === chatbots_check ||
        chatbots_check === 0 ||
        templates_bots_check === 0)
    ) {
      get_bots();
    }
  }, [organization, chatbot?.chatbots?.combinations, dispatch, user]);

  useEffect(() => {
    setBotLoading(true);
    const user_id = user?.user?._id?.["$oid"];
    const get_bots = async () => {
      if (!user_id) return;

      setBotLoading(true);
      try {
        await dispatch(getBotsbyUser(user_id));
      } catch (error) {
        console.error("Error fetching bots:", error);
      } finally {
        setBotLoading(false);
      }
    };
    if (!chatbot?.chatbots?.combinations) {
      get_bots();
    }
    setBotLoading(true);

    const filteredCombinations = (chatbot?.chatbots?.combinations || [])
      .filter((combination) => {
        const hasDefaultFlow =
          combination?.default_flow &&
          Object.keys(combination?.default_flow)?.length > 0;
        return hasDefaultFlow;
      })
      .map((combination) => {
        return {
          id: combination?._id?.["$oid"],
          ...combination,
        };
      });
    console.log("Filtered Combinations:", filteredCombinations);
    setdeployedBotsList(filteredCombinations);
    setTimeout(() => {
      setBotLoading(false);
    }, 5000);
  }, [chatbot?.chatbots?.combinations, dispatch, user?.user?._id]);

  useEffect(() => {
    const urlSegments = window.location.href.split("/");
    const lastSegment = urlSegments[urlSegments.length - 1];
    const botId = lastSegment !== "deployedBots" ? lastSegment : null;

    if (botId) {
      const bot = deployedBotsList.find((bot) => bot.id === botId);
      if (bot) {
        setSelectedBot(bot);
        setShowChat(true);
      }
    } else {
      setShowChat(false);
    }
  }, [deployedBotsList, dispatch, user]);

  const openVersionPicker = (bot, action) => {
    // Turn default_flow into a sorted array
    const versions = Object.entries(bot.default_flow || {})
      .map(([id, meta]) => ({
        id,
        name: meta.name,
        updated_at: meta.updated_at,
        retrieval: meta.retrieval,
        generation: meta.generation,
      }))
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

    // Init state
    setDeployedVersions(versions);
    setSelectedVersionId(versions[0]?.id || "");
    setActionBot(bot);
    setPickerAction(action);
    setVersionPickerOpen(true);
  };

  const handleBotClick = (bot) => {
    navigate(`/dashboard/deployedBots/${bot.id}`);
    setSelectedBot(bot);
    setShowChat(true);
  };

  const handleBackClick = () => {
    setShowChat(false);
    navigate("/dashboard/deployedBots");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleOpenDeleteDialog = (bot_id) => {
    setBotToDelete(bot_id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setBotToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = () => {
    // Logic for deleting the bot deployment goes here
    setOpenDeleteDialog(false);
    handleDeleteDeployedBot();
  };

  const handleDeleteDeployedBot = async () => {
    const default_dict = {};

    const bot_id = botToDelete;
    const user_id = user?.user?._id["$oid"];

    const data = {
      bot_id,
      default_dict,
      user_id,
    };
    try {
      const result = await dispatch(saveDefaultCombinations(data));

      if (result.meta.requestStatus === "fulfilled") {
        setSnackbarMessage("Bot deleted successfully!"); // Show success message
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage("Failed to delete bot."); // Handle any non-fulfilled case
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage("An error occurred while deleting the bot.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true); // Open the snackbar in both cases
      setBotToDelete(null);
    }
  };

  const formattedDate = (uDate) => {
    if (!uDate) return "No Date Available"; // Handle null or undefined dates
    const date = new Date(uDate.$date || uDate); // Support both objects and direct date strings
    const fDate = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
    return fDate;
  };

  const handleOpenSelectedCombinations = async (bot) => {
    if (!bot?.default_flow || Object.keys(bot.default_flow).length === 0)
      return;

    const combos = {};
    const messages = [];

    setSnackbarMessage("Getting Deployed Combination Details");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    setBotLoadingState((prev) => ({ ...prev, [bot?._id["$oid"]]: true }));
    for (const type of ["retrieval", "generation", "finetune"]) {
      const comboId = bot?.default_flow?.[type];
      if (comboId) {
        try {
          const res = await dispatch(
            getCombinationDetails({
              bot_id: bot?._id["$oid"],
              type,
              combo_id: comboId,
            })
          );
          if (res.type.includes("fulfilled")) {
            combos[type] = res.payload.combination_details;
            messages.push(`Details fetched successfully for ${type}`);
            if (type === "generation") {
              combos[type].prompt_combinations = bot?.prompt_combinations || [];
            }
          } else {
            messages.push(`Failed to fetch details for ${type}`);
          }
        } catch (error) {
          console.error(error);
          messages.push(`Error fetching details for ${type}`);
        } finally {
          setBotLoadingState((prev) => ({
            ...prev,
            [bot?._id["$oid"]]: false,
          }));
        }
      }
    }
    setBotLoadingState((prev) => ({ ...prev, [bot?._id["$oid"]]: false }));
    // Set combinations and show all messages in snackbar
    setShowBotCombination(combos);
    setShowSelectedCombinations(true);
    setSnackbarMessage(messages.join(". "));
    setSnackbarSeverity("success");
  };

  const handleCloseSelectedCombinations = () => {
    setShowBotCombination(null);
    setShowSelectedCombinations(false);
  };

  function mapSequence(sequence) {
    const mapping = {
      basic: "Basic",
      multi_query: "Multi Phrasing",
      hyde: "HyDE",
      query_expansion: "Expansion",
      complex_to_simple: "Deconstruction",
      iterative_deconstruction: "Iterative Deconstruction",
    };

    if (sequence) {
      // Check if the sequence contains the arrow symbol
      if (sequence.includes("→")) {
        return sequence
          .split("→")
          .map((item) => mapping[item.toLowerCase()] || item)
          .join(" → ");
      }

      // Return the original sequence if no arrow symbol is found
      return mapping[sequence.toLowerCase()];
    } else {
      return "N/A";
    }
  }

  function mapDocumentParser(parser) {
    const mapping = {
      llama_parse: "Llama Parse",
      recursive_character_text_splitter: "Basic",
      unstructuredio: "Unstructured.io",
    };

    return mapping[parser] || "N/A";
  }

  const formatValue = (value) => {
    if (value === 0) return value; // Return 0 as is

    const exponent = Math.floor(Math.log10(Math.abs(value)));
    const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

    // If the exponent is between -2 and 2 (inclusive), return the rounded value with 2 decimal places
    if (exponent >= -2 && exponent <= 2) {
      return Number(value).toFixed(2);
    }

    // Otherwise, return the mantissa and exponent in scientific notation
    return (
      <>
        {mantissa} &times; 10<sup>{exponent}</sup>
      </>
    );
  };

  // Fetch & show combination details for one specific version
  const actualViewCombination = async (bot, versionId) => {
    const flowMeta = bot.default_flow[versionId];
    if (!flowMeta) return;

    // reuse your combination‐fetch loop, swapping out comboId = flowMeta[type]
    const combos = {};
    const messages = [];
    setSnackbarMessage("Fetching details…");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    setBotLoadingState((st) => ({ ...st, [bot.id]: true }));
    for (const type of ["retrieval", "generation", "finetune"]) {
      const comboId = flowMeta[type];
      if (!comboId) continue;
      try {
        const res = await dispatch(
          getCombinationDetails({
            bot_id: bot.id,
            type,
            combo_id: comboId,
          })
        );
        if (res.type.endsWith("fulfilled")) {
          combos[type] = res.payload.combination_details;
          messages.push(`${type} loaded`);
          if (type === "generation") {
            combos[type].prompt_combinations = bot.prompt_combinations || [];
          }
        } else {
          messages.push(`${type} failed`);
        }
      } catch {
        messages.push(`${type} error`);
      }
    }
    setBotLoadingState((st) => ({ ...st, [bot.id]: false }));
    setShowBotCombination(combos);
    setShowSelectedCombinations(true);
    setSnackbarMessage(messages.join(", "));
    setSnackbarSeverity("success");
  };

  // Delete just one deployed version
  const actualDeleteVersion = async (botId, versionId) => {
    // build whatever payload your backend expects
    const data = {
      bot_id: botId,
      default_dict: {},
      remove: versionId,
    };
    const res = await dispatch(saveDefaultCombinations(data));
    if (res.meta.requestStatus === "fulfilled") {
      setSnackbarMessage("Version deleted!");
      setSnackbarSeverity("success");
      // optionally remove from your local list:
      setdeployedBotsList((list) =>
        list.map((b) =>
          b.id === botId
            ? {
                ...b,
                default_flow: Object.fromEntries(
                  Object.entries(b.default_flow).filter(
                    ([id]) => id !== versionId
                  )
                ),
              }
            : b
        )
      );
    } else {
      setSnackbarMessage("Delete failed");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  return (
    <Container sx={DeployedBotsContainerStyles}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Confirm Delete */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        PaperProps={{ sx: dialogStyles.dialogRoot }}
      >
        <DialogTitle sx={{ ...dialogStyles.dialogTitle, mb: 2 }}>
          Confirm Deployed Bot Deletion
        </DialogTitle>
        <DialogContent dividers sx={dialogStyles.dialogContent}>
          <DialogContentText sx={dialogStyles.dialogContentText}>
            Do you want to delete the previous deployment of the bot?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button onClick={handleCloseDeleteDialog} sx={secondaryButtonStyles}>
            No
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={primaryButtonStyles}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Combinations */}
      <Dialog
        open={showSelectedCombinations}
        onClose={handleCloseSelectedCombinations}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: dialogStyles.dialogRoot }}
      >
        <DialogTitle sx={dialogStyles.dialogTitle}>
          Selected Combinations
        </DialogTitle>
        <DialogContent sx={dialogStyles.dialogContent}>
          {showBotCombination?.retrieval && (
            <TableContainer sx={DeployedBotsTableContainerStyles}>
              <Box
                sx={{
                  display: "flex",
                  padding: "20px",
                  justifyContent: "center",
                  borderBottom: `1px solid ${theme.border.divider}`,
                }}
              >
                <Typography
                  sx={{
                    color: theme.textColors.dark.primary,
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  Retrieval Combination
                </Typography>
              </Box>
              <Table>
                <TableHead sx={tableStyles.tableHead}>
                  <TableRow>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Chunk Size
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Metric Type
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Retrieval Method
                    </TableCell>

                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Post Retrieval
                    </TableCell>
                    <TableCell
                      sx={tableStyles.tableHeaderCell}
                      align="center"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Top K
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Embedding Model
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Document Parser
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Query Transformations
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Retrieval %
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Cost ($)
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      align="center"
                    >
                      Latency (s)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{
                      ...tableStyles.tableRow,
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.chunksize}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.metric}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.retrieval_method ===
                      "complex-to-simple-query"
                        ? "deconstruction"
                        : showBotCombination?.retrieval?.retrieval_method}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.rerank_method}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.top_k}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.embedding_model}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {mapDocumentParser(showBotCombination?.retrieval?.parser)}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {mapSequence(
                        showBotCombination?.retrieval?.query_preprocess_method
                      )}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.average_rating}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableCell,
                        whiteSpace: "nowrap",
                      }}
                      align="center"
                    >
                      {showBotCombination?.completed === true &&
                        showBotCombination?.retrieval?.cost}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.retrieval?.completed === true &&
                        showBotCombination?.retrieval?.latency}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {showBotCombination?.generation && (
            <TableContainer sx={DeployedBotsTableContainerStyles}>
              <Box
                sx={{
                  display: "flex",
                  padding: " 20px",
                  justifyContent: "center",
                  borderBottom: `1px solid ${theme.border.divider}`,
                }}
              >
                <Typography
                  sx={{
                    color: colors.extremeWhite,
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  Foundational LLM Combination
                </Typography>
              </Box>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      LLM
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      align="center"
                    >
                      Prompt Type
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Prompt
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Temperature
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      align="center"
                    >
                      Output Tokens Limit
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Accuracy (%)
                    </TableCell>
                    {/* <TableCell  sx={tableStyles.tableHeaderCell} align="center">Status</TableCell> */}
                    <TableCell sx={tableStyles.tableHeaderCell} align="center">
                      Cost ($)
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      align="center"
                    >
                      Latency (s)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{
                      ...tableStyles.tableRow,
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.generation.llm}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.generation.prompts}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      <Tooltip
                        title={
                          showBotCombination?.generation?.prompt_combinations[
                            showBotCombination?.generation.prompts
                          ] || ""
                        }
                        arrow
                      >
                        <span>
                          {showBotCombination?.generation?.prompt_combinations[
                            showBotCombination?.generation.prompts
                          ]
                            ? showBotCombination?.generation
                                ?.prompt_combinations[
                                showBotCombination?.generation.prompts
                              ].length > 40
                              ? showBotCombination?.generation?.prompt_combinations[
                                  showBotCombination?.generation.prompts
                                ].substring(0, 40) + "..."
                              : showBotCombination?.generation
                                  ?.prompt_combinations[
                                  showBotCombination?.generation.prompts
                                ]
                            : ""}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.generation.temperature}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.generation.outputTokenLimit === "0"
                        ? "N/A"
                        : showBotCombination?.generation.outputTokenLimit}
                    </TableCell>

                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.generation?.average_rating}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableCell,
                        whiteSpace: "nowrap",
                      }}
                      align="center"
                    >
                      {formatValue(
                        showBotCombination?.generation?.inference_cost
                      )}
                    </TableCell>
                    <TableCell sx={tableStyles.tableCell} align="center">
                      {showBotCombination?.generation?.completed === true &&
                        showBotCombination?.generation?.latency}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={versionPickerOpen}
        onClose={() => setVersionPickerOpen(false)}
        PaperProps={{ sx: dialogStyles.dialogRoot }}
      >
        <DialogTitle sx={dialogStyles.dialogTitle}>
          {pickerAction === "view"
            ? "Select version to View"
            : "Select version to Delete"}
        </DialogTitle>

        <DialogContent sx={dialogStyles.dialogContent}>
          <FormControl fullWidth variant="standard">
            <Select
              value={selectedVersionId}
              onChange={(e) => setSelectedVersionId(e.target.value)}
              disableUnderline
              sx={{
                color: colors.extremeWhite,
                "& .MuiSvgIcon-root": { color: colors.extremeWhite },
                ".MuiSelect-select": { py: 1 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: { backgroundColor: colors.extremeBlue },
                },
              }}
            >
              {deployedVersions.map((v) => (
                <MenuItem
                  key={v.id}
                  value={v.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography sx={{ color: colors.extremeWhite }}>
                    {v.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.lightGray }}
                  >
                    {formattedDate(v.updated_at)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={() => setVersionPickerOpen(false)}
            sx={secondaryButtonStyles}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setVersionPickerOpen(false);
              if (pickerAction === "view") {
                await actualViewCombination(actionBot, selectedVersionId);
              } else {
                await actualDeleteVersion(actionBot.id, selectedVersionId);
              }
            }}
            sx={primaryButtonStyles}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {isBotsLoading ? (
        <Box
          sx={{
            ...DeployedBotsPaperStyles,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.textColors.dark.primary,
              fontSize: theme.typography.fontSize.xl,
            }}
          >
            Loading deployed LLM applications...
          </Typography>
        </Box>
      ) : deployedBotsList?.length > 0 ? (
        !showChat ? (
          <Box sx={DeployedBotsPaperStyles}>
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontFamily: theme.typography.fontFamily,
                fontWeight: theme.typography.fontWeight.bold,
                fontSize: theme.typography.fontSize.xl,
                color: theme.textColors.dark.primary,
              }}
            >
              Select a Bot to start Conversation
            </Typography>
            <TableContainer sx={DeployedBotsTableContainerStyles}>
              <Table>
                <TableHead sx={tableStyles.tableHead}>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                      }}
                      align="left"
                    >
                      Bot Name
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="left">
                      Description
                    </TableCell>
                    <TableCell sx={tableStyles.tableHeaderCell} align="left">
                      Last Updated
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.tableHeaderCell,
                        position: "sticky",
                        right: 0,
                        zIndex: 1,
                      }}
                      align="left"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deployedBotsList.map((bot) => (
                    <TableRow
                      key={bot.id}
                      sx={{
                        ...tableStyles.tableRow,
                        "&:last-child td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell
                        sx={{
                          ...tableStyles.tableCell,
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                        }}
                        align="left"
                      >
                        {bot.name}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {bot.problem_statement}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {formattedDate(bot.updated_at)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableStyles.tableCell,
                          position: "sticky",
                          right: 0,
                          zIndex: 1,
                        }}
                        align="left"
                      >
                        <Tooltip title="View Selected Combinations">
                          <IconButton
                            onClick={() => openVersionPicker(bot, "view")}
                            sx={{
                              ":hover": {
                                color: colors.primaryBlue,
                              },
                              color: colors.extremeWhite,
                              mr: 2,
                            }}
                            disabled={isAnyBotLoading}
                          >
                            {botLoadingState[bot.id] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ViewTimeline fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Chat with Deployed Bot">
                          <IconButton
                            onClick={() => handleBotClick(bot)}
                            sx={{
                              ":hover": {
                                color: colors.primaryBlue,
                              },
                              color: colors.extremeWhite,
                              mr: 2,
                            }}
                            disabled={isAnyBotLoading}
                          >
                            <Chat fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(user?.user?.privilege === "owner" ||
                          user?.user?.privilege === "admin") && (
                          <Tooltip title="Delete Deployed Bot">
                            <IconButton
                              onClick={() => openVersionPicker(bot, "delete")}
                              sx={{
                                ":hover": {
                                  color: "red",
                                },
                                color: colors.extremeWhite,
                              }}
                              disabled={isAnyBotLoading}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <ChatBot
            selectedBot={selectedBot}
            handleBackClick={handleBackClick}
          />
        )
      ) : botLoading ? null : (
        <Box
          sx={{
            ...DeployedBotsPaperStyles,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.textColors.dark.primary,
              fontSize: theme.typography.fontSize.xl,
            }}
          >
            No Bots Deployed
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default DeployedBots;
